import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import { EmptyState, PageHeader } from "../../components/dashboard/DashboardUI";
import Loader from "../../components/loader/Loader";
import { getServices, updateService } from "../../redux/actions/serviceActions";
import { formatPrice } from "../../utils/formatPrice";

const toast = (icon, title, text) =>
  Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 4000,
  }).fire({ icon, title, text });

const isLifetime = (billings) =>
  Array.isArray(billings) && billings.includes("lifetime");

// Price shown in the list, per unit for services sold by quantity
const priceLabel = (service) => {
  if (service.throughEstimation) return "Sur devis";
  if (isLifetime(service.billings)) {
    return `${formatPrice(service.uniquePrice)}${
      service.minQuantity ? " / unité" : ""
    }`;
  }
  return [
    service.monthlyPrice && `${formatPrice(service.monthlyPrice)} / mois`,
    service.yearlyPrice &&
      `${formatPrice(service.yearlyPrice)} / mois en annuel`,
  ]
    .filter(Boolean)
    .join(" · ");
};

// The yearly price is a monthly rate, charged 12 times at payment
// Services catalogue: billing type and prices
function Services() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const { services } = useSelector((state) => state.services);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!user?.token) return;

    setLoading(true);
    Promise.resolve(dispatch(getServices(user.token))).finally(() =>
      setLoading(false),
    );
  }, [user?.token, dispatch]);

  const term = search.trim().toLowerCase();
  const rows = (services || []).filter(
    (service) =>
      !term ||
      service.name?.toLowerCase().includes(term) ||
      service.code?.toLowerCase().includes(term),
  );

  const open = (service) => {
    setSelected(service);
    setForm({
      billings: isLifetime(service.billings)
        ? ["lifetime"]
        : ["monthly", "yearly"],
      monthlyPrice: service.monthlyPrice || 0,
      yearlyPrice: service.yearlyPrice || 0,
      uniquePrice: service.uniquePrice || 0,
      throughEstimation: Boolean(service.throughEstimation),
    });
  };

  const close = () => {
    setSelected(null);
    setForm(null);
  };

  const handleCallback = ({ success, message }) => {
    setSubmitting(false);
    if (!success) {
      toast("error", "Échec", message);
      return;
    }
    close();
    toast("success", "Service mis à jour", message);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitting(true);
    // The API refuses a unique price mixed with subscription prices
    const payload = isLifetime(form.billings)
      ? { ...form, monthlyPrice: 0, yearlyPrice: 0 }
      : { ...form, uniquePrice: 0, throughEstimation: false };
    dispatch(updateService(user.token, selected.code, payload, handleCallback));
  };

  const setNumber = (field) => (event) =>
    setForm({ ...form, [field]: Number(event.target.value) });

  const lifetime = form && isLifetime(form.billings);

  return (
    <>
      <Loader isLoading={submitting} />
      <PageHeader
        title="Catalogue des services"
        lead="Type de facturation et prix de chaque service."
      />

      <div className="dash-card">
        <div className="dash-filters">
          <input
            type="search"
            placeholder="Rechercher un service ou un code"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Rechercher"
          />
        </div>

        {loading ? (
          <p className="dash-loading">Chargement des services…</p>
        ) : rows.length === 0 ? (
          <EmptyState title="Aucun service">
            Aucun service ne correspond à votre recherche.
          </EmptyState>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table is-responsive">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Facturation</th>
                  <th>Prix</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((service) => (
                  <tr key={service.code}>
                    <td>
                      <span className="dash-cell-title">{service.name}</span>
                      <span className="dash-cell-sub">{service.code}</span>
                    </td>
                    <td data-label="Facturation">
                      {isLifetime(service.billings)
                        ? "Paiement unique"
                        : "Abonnement"}
                    </td>
                    <td data-label="Prix">{priceLabel(service) || "—"}</td>
                    <td>
                      <div className="dash-row-actions">
                        <button
                          type="button"
                          className="dash-link-button"
                          onClick={() => open(service)}
                        >
                          Modifier
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal show={Boolean(selected)} onHide={close} centered>
        {selected && form && (
          <>
            <Modal.Header closeButton>
              <div className="dash-modal-head">
                <Modal.Title as="h2" className="dash-card-title mb-0">
                  {selected.name}
                </Modal.Title>
                <span className="dash-cell-sub">{selected.code}</span>
              </div>
            </Modal.Header>
            <Modal.Body>
              <form className="dash-form" onSubmit={handleSubmit}>
                <label htmlFor="service-billing">Facturation</label>
                <select
                  id="service-billing"
                  value={lifetime ? "lifetime" : "subscription"}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      billings:
                        event.target.value === "lifetime"
                          ? ["lifetime"]
                          : ["monthly", "yearly"],
                    })
                  }
                >
                  <option value="subscription">
                    Abonnement mensuel et annuel
                  </option>
                  <option value="lifetime">Paiement unique</option>
                </select>

                {lifetime ? (
                  <>
                    <label htmlFor="service-unique">
                      {selected.minQuantity
                        ? "Prix unitaire (FCFA)"
                        : "Prix (FCFA)"}
                    </label>
                    <input
                      id="service-unique"
                      type="number"
                      min="0"
                      value={form.uniquePrice}
                      onChange={setNumber("uniquePrice")}
                    />
                    <label className="dash-check">
                      <input
                        type="checkbox"
                        checked={form.throughEstimation}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            throughEstimation: event.target.checked,
                          })
                        }
                      />
                      Prix fixé sur devis
                    </label>
                  </>
                ) : (
                  <>
                    <label htmlFor="service-monthly">
                      Formule mensuelle : prix par mois (FCFA)
                    </label>
                    <input
                      id="service-monthly"
                      type="number"
                      min="0"
                      value={form.monthlyPrice}
                      onChange={setNumber("monthlyPrice")}
                    />
                    <label htmlFor="service-yearly">
                      Formule annuelle : prix par mois (FCFA)
                    </label>
                    <input
                      id="service-yearly"
                      type="number"
                      min="0"
                      value={form.yearlyPrice}
                      onChange={setNumber("yearlyPrice")}
                    />
                  </>
                )}

                <div className="dash-form-actions">
                  <button
                    type="button"
                    className="dash-button is-secondary"
                    onClick={close}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="dash-button"
                    disabled={submitting}
                  >
                    Enregistrer
                  </button>
                </div>
              </form>
            </Modal.Body>
          </>
        )}
      </Modal>
    </>
  );
}

export default Services;
